import React from 'react';
import { Progress, Row, Col, Typography, Tooltip } from 'antd';

const { Text, Title } = Typography;

const BiologicalAgeBreakdownChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Text type="secondary">No breakdown data available.</Text>
      </div>
    );
  }

  // Helper function to get health-based color gradient
  const getHealthColor = (contribution) => {
    // Convert contribution to a percentage-like scale for color mapping
    // Negative contributions (good) = green, positive (bad) = red
    if (contribution <= -2) return '#52c41a'; // Green (excellent)
    if (contribution <= -1) return '#73d13d'; // Light green (good)
    if (contribution <= 0) return '#faad14';  // Yellow (neutral)
    if (contribution <= 1) return '#fa8c16';  // Orange (concerning)
    if (contribution <= 2) return '#ff4d4f';  // Red (poor)
    return '#f5222d'; // Dark red (very poor)
  };

  // Helper function to convert contribution to percentage for progress circle
  const getProgressPercentage = (contribution) => {
    // Map contribution range to 0-100% for visual representation
    // We'll use absolute value and cap at reasonable ranges
    const absContribution = Math.abs(contribution);
    const maxContribution = 5; // Reasonable max for visualization
    const percentage = Math.min((absContribution / maxContribution) * 100, 100);
    return Math.round(percentage);
  };

  const chartData = data.map(item => ({
    name: item.system.charAt(0).toUpperCase() + item.system.slice(1),
    contribution: parseFloat(item.penalty.toFixed(2)),
    color: getHealthColor(parseFloat(item.penalty.toFixed(2))),
    percentage: getProgressPercentage(parseFloat(item.penalty.toFixed(2)))
  }));

  return (
    <div style={{ padding: '20px' }}>
      <Title level={4} style={{ textAlign: 'center', marginBottom: '24px', color: '#2c3e50' }}>
        Health System Age Contributions
      </Title>

      <Row gutter={[24, 24]}>
        {chartData.map((item, index) => (
          <Col xs={12} sm={8} md={6} key={index}>
            <div style={{ textAlign: 'center' }}>
              <Tooltip
                title={
                  <div>
                    <div><strong>{item.name} System</strong></div>
                    <div>Age Contribution: {item.contribution > 0 ? '+' : ''}{item.contribution} years</div>
                    <div style={{ marginTop: '4px', fontSize: '12px' }}>
                      {item.contribution > 0
                        ? 'This system is aging you faster than expected'
                        : item.contribution < 0
                          ? 'This system is helping you age slower'
                          : 'This system has neutral impact on aging'
                      }
                    </div>
                  </div>
                }
                placement="top"
              >
                <Progress
                  type="circle"
                  percent={item.percentage}
                  format={() => (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: item.color,
                        lineHeight: '1.2'
                      }}>
                        {item.contribution > 0 ? '+' : ''}{item.contribution}
                      </div>
                      <div style={{
                        fontSize: '10px',
                        color: '#666',
                        lineHeight: '1.2'
                      }}>
                        years
                      </div>
                    </div>
                  )}
                  strokeColor={item.color}
                  size={80}
                  trailColor="#f0f2f5"
                  strokeWidth={8}
                />
              </Tooltip>

              <div style={{
                marginTop: '8px',
                fontSize: '12px',
                fontWeight: '500',
                color: '#2c3e50',
                textAlign: 'center'
              }}>
                {item.name}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <div style={{
        marginTop: '24px',
        padding: '16px',
        backgroundColor: '#f9f9f9',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Each circle shows how much each health system contributes to your biological age.
          Negative values (green) indicate systems that are aging slower than expected,
          while positive values (red/orange) indicate systems aging faster than expected.
        </Text>
      </div>
    </div>
  );
};

export default BiologicalAgeBreakdownChart;
